import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAssistedLiving,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBarbecue,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBicycleStorage,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCeilingFan,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDisabledAccess,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDryCleaningDropOff,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasElevator,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGuestSuite,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHotTub,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineMaintenancePortal,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineRentPayment,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnsiteManagement,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPackageService,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetPark,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSauna,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSpanishSpeakingStaff,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasStorageSpace,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSwimmingPool,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTwentyFourHourMaintenance,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasValetTrash,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_isOver55,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_smokingAllowed,
  useRentalManagerUserPropertyEditV2WebApiHandler_isIncomeRestricted,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave, getIsInputFormDisabled } from '../../utils';

import {
  PROPERTY_SERVICE_KEYS,
  PROPERTY_SERVICE_METADATA,
  type PropertyServiceKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';
import DisabledAccessAmenityTooltip from '../../../DisabledAccessAmenityTooltip';

type LocalState = Record<PropertyServiceKey, boolean>;

const PropertyServicesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const buildingAttributes = listingDetails?.buildingAttributes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasTwentyFourHourMaintenance: buildingAttributes?.hasTwentyFourHourMaintenance ?? false,
    hasAssistedLiving: buildingAttributes?.hasAssistedLiving ?? false,
    hasBarbecue: buildingAttributes?.hasBarbecue ?? false,
    hasBicycleStorage: buildingAttributes?.hasBicycleStorage ?? false,
    hasCeilingFan: buildingAttributes?.hasCeilingFan ?? false,
    hasDisabledAccess: buildingAttributes?.hasDisabledAccess ?? false,
    hasDryCleaningDropOff: buildingAttributes?.hasDryCleaningDropOff ?? false,
    hasElevator: buildingAttributes?.hasElevator ?? false,
    hasGuestSuite: buildingAttributes?.hasGuestSuite ?? false,
    hasHotTub: buildingAttributes?.hasHotTub ?? false,
    isIncomeRestricted: buildingAttributes?.isIncomeRestricted ?? false,
    hasOnlineMaintenancePortal: buildingAttributes?.hasOnlineMaintenancePortal ?? false,
    hasOnlineRentPayment: buildingAttributes?.hasOnlineRentPayment ?? false,
    hasOnsiteManagement: buildingAttributes?.hasOnsiteManagement ?? false,
    isOver55: buildingAttributes?.isOver55 ?? false,
    hasPackageService: buildingAttributes?.hasPackageService ?? false,
    hasPetPark: buildingAttributes?.hasPetPark ?? false,
    hasSauna: buildingAttributes?.hasSauna ?? false,
    smokingAllowed: buildingAttributes?.smokingAllowed ?? false,
    hasSpanishSpeakingStaff: buildingAttributes?.hasSpanishSpeakingStaff ?? false,
    hasStorageSpace: buildingAttributes?.hasStorageSpace ?? false,
    hasSwimmingPool: buildingAttributes?.hasSwimmingPool ?? false,
    hasValetTrash: buildingAttributes?.hasValetTrash ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Property services updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateTwentyFourHourMaintenance } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTwentyFourHourMaintenance,
    skipToast: true,
    meta: { context: { api: 'hasTwentyFourHourMaintenance', details: { listingAlias } } },
  });

  const { mutateAsync: updateAssistedLiving } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasAssistedLiving,
    skipToast: true,
    meta: { context: { api: 'hasAssistedLiving', details: { listingAlias } } },
  });

  const { mutateAsync: updateBarbecue } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBarbecue,
    skipToast: true,
    meta: { context: { api: 'hasBarbecue', details: { listingAlias } } },
  });

  const { mutateAsync: updateBicycleStorage } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasBicycleStorage,
    skipToast: true,
    meta: { context: { api: 'hasBicycleStorage', details: { listingAlias } } },
  });

  const { mutateAsync: updateCeilingFan } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCeilingFan,
    skipToast: true,
    meta: { context: { api: 'hasCeilingFan', details: { listingAlias } } },
  });

  const { mutateAsync: updateDisabledAccess } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDisabledAccess,
    skipToast: true,
    meta: { context: { api: 'hasDisabledAccess', details: { listingAlias } } },
  });

  const { mutateAsync: updateDryCleaningDropOff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDryCleaningDropOff,
    skipToast: true,
    meta: { context: { api: 'hasDryCleaningDropOff', details: { listingAlias } } },
  });

  const { mutateAsync: updateElevator } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasElevator,
    skipToast: true,
    meta: { context: { api: 'hasElevator', details: { listingAlias } } },
  });

  const { mutateAsync: updateGuestSuite } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGuestSuite,
    skipToast: true,
    meta: { context: { api: 'hasGuestSuite', details: { listingAlias } } },
  });

  const { mutateAsync: updateHotTub } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasHotTub,
    skipToast: true,
    meta: { context: { api: 'hasHotTub', details: { listingAlias } } },
  });

  const { mutateAsync: updateIncomeRestricted } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_isIncomeRestricted,
    skipToast: true,
    meta: { context: { api: 'isIncomeRestricted', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnlineMaintenancePortal } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineMaintenancePortal,
    skipToast: true,
    meta: { context: { api: 'hasOnlineMaintenancePortal', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnlineRentPayment } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnlineRentPayment,
    skipToast: true,
    meta: { context: { api: 'hasOnlineRentPayment', details: { listingAlias } } },
  });

  const { mutateAsync: updateOnsiteManagement } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasOnsiteManagement,
    skipToast: true,
    meta: { context: { api: 'hasOnsiteManagement', details: { listingAlias } } },
  });

  const { mutateAsync: updateOver55 } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_isOver55,
    skipToast: true,
    meta: { context: { api: 'isOver55', details: { listingAlias } } },
  });

  const { mutateAsync: updatePackageService } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPackageService,
    skipToast: true,
    meta: { context: { api: 'hasPackageService', details: { listingAlias } } },
  });

  const { mutateAsync: updatePetPark } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPetPark,
    skipToast: true,
    meta: { context: { api: 'hasPetPark', details: { listingAlias } } },
  });

  const { mutateAsync: updateSauna } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSauna,
    skipToast: true,
    meta: { context: { api: 'hasSauna', details: { listingAlias } } },
  });

  const { mutateAsync: updateSmokingAllowed } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_smokingAllowed,
    skipToast: true,
    meta: { context: { api: 'smokingAllowed', details: { listingAlias } } },
  });

  const { mutateAsync: updateSpanishSpeakingStaff } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSpanishSpeakingStaff,
    skipToast: true,
    meta: { context: { api: 'hasSpanishSpeakingStaff', details: { listingAlias } } },
  });

  const { mutateAsync: updateStorageSpace } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasStorageSpace,
    skipToast: true,
    meta: { context: { api: 'hasStorageSpace', details: { listingAlias } } },
  });

  const { mutateAsync: updateSwimmingPool } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasSwimmingPool,
    skipToast: true,
    meta: { context: { api: 'hasSwimmingPool', details: { listingAlias } } },
  });

  const { mutateAsync: updateValetTrash } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasValetTrash,
    skipToast: true,
    meta: { context: { api: 'hasValetTrash', details: { listingAlias } } },
  });

  const mutationMap = {
    hasTwentyFourHourMaintenance: updateTwentyFourHourMaintenance,
    hasAssistedLiving: updateAssistedLiving,
    hasBarbecue: updateBarbecue,
    hasBicycleStorage: updateBicycleStorage,
    hasCeilingFan: updateCeilingFan,
    hasDisabledAccess: updateDisabledAccess,
    hasDryCleaningDropOff: updateDryCleaningDropOff,
    hasElevator: updateElevator,
    hasGuestSuite: updateGuestSuite,
    hasHotTub: updateHotTub,
    isIncomeRestricted: updateIncomeRestricted,
    hasOnlineMaintenancePortal: updateOnlineMaintenancePortal,
    hasOnlineRentPayment: updateOnlineRentPayment,
    hasOnsiteManagement: updateOnsiteManagement,
    isOver55: updateOver55,
    hasPackageService: updatePackageService,
    hasPetPark: updatePetPark,
    hasSauna: updateSauna,
    smokingAllowed: updateSmokingAllowed,
    hasSpanishSpeakingStaff: updateSpanishSpeakingStaff,
    hasStorageSpace: updateStorageSpace,
    hasSwimmingPool: updateSwimmingPool,
    hasValetTrash: updateValetTrash,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as PropertyServiceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = PROPERTY_SERVICE_METADATA[fieldName];
      await mutationMap[fieldName]({
        postData: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Property services</Legend>} disabled={isInputFormDisabled}>
      {PROPERTY_SERVICE_KEYS.map((fieldName) => {
        const metadata = PROPERTY_SERVICE_METADATA[fieldName];

        return (
          <LabeledControl
            key={`otherTypes-${metadata.label}`}
            label={
              fieldName === 'hasDisabledAccess' ? (
                <>
                  <Label>{metadata.label}</Label>
                  <DisabledAccessAmenityTooltip />
                </>
              ) : (
                <Label>{metadata.label}</Label>
              )
            }
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(PropertyServicesAmenities);
